import { API_METHODS, callApi, createApiFunction } from 'api/apiProvider'
import { mixtilesAxios } from 'utils/ApiUtils'
import z from 'zod'

const PresignedUrlSchema = z.object({
  uploadURL: z.string(),
  downloadURL: z.string(),
})
const PresignedURLResponseSchema = PresignedUrlSchema.or(
  z.array(PresignedUrlSchema)
)
export type PresignedURLResponseType = z.infer<
  typeof PresignedURLResponseSchema
>
export type PresignedURLType = z.infer<typeof PresignedUrlSchema>

export async function getPresignedURLForAIUpload(
  count: number = 1
): Promise<PresignedURLResponseType> {
  const data = await callApi(
    createApiFunction(API_METHODS.POST, '/v1/ai/presigned-url-for-upload', {
      count,
    })
  )
  return PresignedURLResponseSchema.parse(data)
}

// url is the unsigned photo URL to keep on tiles and covers; preview is a signed link that expires
export const AiGenJobResultSchema = z.object({
  url: z.string(),
  preview: z.string(),
  status: z.enum(['success', 'error']).default('success'),
  ai_error_code: z.string().optional(),
})

export const AiGenJobResponseSchema = z
  .object({
    jobId: z.string(),
    state: z.enum(['created', 'processing', 'completed', 'partial', 'error']),
    urls: z.array(AiGenJobResultSchema),
  })
  .and(
    z.discriminatedUnion('state', [
      z.object({
        state: z.enum(['completed', 'partial']),
        width: z.number(),
        height: z.number(),
      }),
      z.object({
        state: z.literal('error'),
        ai_error_code: z.string(),
        width: z.number().optional(),
        height: z.number().optional(),
      }),
      z.object({
        state: z.enum(['created', 'processing']),
        width: z.number().optional(),
        height: z.number().optional(),
      }),
    ])
  )
  .transform((response) =>
    response.state === 'partial'
      ? { ...response, state: 'completed' as const }
      : response
  )
export type AiGenJobResponseType = z.infer<typeof AiGenJobResponseSchema>

export async function getGenerationStatus(
  generationId: string
): Promise<AiGenJobResponseType> {
  const data = await callApi(
    createApiFunction(API_METHODS.GET, `/v1/ai/job/${generationId}`)
  )
  return AiGenJobResponseSchema.parse(data)
}

const GenerateAIImageResponseSchema = z.object({
  jobId: z.string(),
  state: z.enum(['completed', 'processing', 'created']),
})
type GenerateAIImageResponseType = z.infer<typeof GenerateAIImageResponseSchema>

export async function generateAIImage({
  templateSlug,
  imageUrls,
  imageDimensions,
  userText,
  amplitudeDeviceId,
  variant,
  variantIndex,
  variantIndexes,
  lowQuality,
  form,
  useAiWorkflows,
  productFlow,
}: {
  templateSlug: string
  imageUrls?: string[]
  imageDimensions?: [number, number][]
  userText?: string
  amplitudeDeviceId: string | undefined
  variant?: string
  // Request a single specific variation (one job = one image); omit for the
  // legacy multi-variation behavior.
  variantIndex?: number
  // Request several specific variations in one job, one image each, returned in this order.
  variantIndexes?: number[]
  lowQuality?: boolean
  form?: Record<string, string>
  useAiWorkflows?: boolean
  // Stored on the gen job as product_flow, which is how BI tells which product flow a generation came from.
  productFlow?: string
}): Promise<GenerateAIImageResponseType> {
  const data = await callApi(
    createApiFunction(API_METHODS.POST, '/v1/ai/generate', {
      templateSlug,
      imageUrls,
      imageDimensions,
      userText,
      amplitudeDeviceId,
      variant,
      variantIndex,
      variantIndexes,
      lowQuality,
      form,
      ...(useAiWorkflows ? { useAiWorkflows } : {}),
      productFlow,
    })
  )
  return GenerateAIImageResponseSchema.parse(data)
}

// Only the fields consumers need; zod strips the rest of each template object.
const AiTemplateSummarySchema = z.object({
  slug: z.string(),
  estimated_time: z.number().optional(),
})
export type AiTemplateSummary = z.infer<typeof AiTemplateSummarySchema>

export async function getAiTemplates(): Promise<AiTemplateSummary[]> {
  const { data } = await mixtilesAxios.get('/v1/ai/templates')
  return z.array(AiTemplateSummarySchema).parse(data)
}
